import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

function VoucherShow() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [voucher, setVoucher] = useState(null);
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        loadVoucher();
    }, [id]);

    const loadVoucher = async () => {
        try {
            const response = await accountingApi.getVoucher(id);
            if (response.success) {
                const voucherData = response.data?.voucher || response.data;
                setVoucher(voucherData);
            }
        } catch (error) {
            console.error('Error loading voucher:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePost = async () => {
        try {
            setPosting(true);
            const response = await accountingApi.postVoucher(id);
            if (response.success) {
                loadVoucher();
            } else {
                alert(response.message || 'Failed to post voucher');
            }
        } catch (error) {
            alert('Failed to post voucher');
        } finally {
            setPosting(false);
        }
    };

    const handleUnpost = async () => {
        try {
            setPosting(true);
            const response = await accountingApi.unpostVoucher(id);
            if (response.success) {
                loadVoucher();
            } else {
                alert(response.message || 'Failed to unpost voucher');
            }
        } catch (error) {
            alert('Failed to unpost voucher');
        } finally {
            setPosting(false);
        }
    };

    if (loading) return <div className="p-4 md:p-8 text-center">Loading...</div>;
    if (!voucher) return <div className="p-4 md:p-8 text-center">Voucher not found</div>;

    return (
        <div className="max-w-5xl mx-auto px-3 md:px-4 py-4 md:py-8">
            {/* Header */}
            <div className="mb-4 md:mb-6">
                <h1 className="text-xl md:text-3xl font-bold text-slate-800 mb-1 md:mb-2">Voucher Details</h1>
                <p className="text-sm text-slate-600">View voucher information and entries</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 md:gap-3 mb-4 md:mb-6">
                <Link
                    to="/accounting/vouchers"
                    className="px-3 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
                >
                    ← Back
                </Link>
                {!voucher.is_posted && (
                    <Link
                        to={`/accounting/vouchers/${id}/edit`}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                        Edit
                    </Link>
                )}
            </div>

            {/* Voucher Information Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-6 mb-4 md:mb-6">
                <h2 className="text-base md:text-lg font-bold text-slate-800 mb-4">Voucher Information</h2>

                {/* Mobile: Stack layout */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    <div>
                        <label className="block text-xs md:text-sm font-medium text-slate-500 mb-1">Voucher No</label>
                        <p className="text-sm md:text-base font-semibold text-slate-900 break-all">{voucher.voucher_number}</p>
                    </div>

                    <div>
                        <label className="block text-xs md:text-sm font-medium text-slate-500 mb-1">Date</label>
                        <p className="text-sm md:text-base text-slate-900">{voucher.voucher_date}</p>
                    </div>

                    <div>
                        <label className="block text-xs md:text-sm font-medium text-slate-500 mb-1">Type</label>
                        <p className="text-sm md:text-base text-slate-900">{voucher.voucher_type?.name || '-'}</p>
                    </div>

                    <div>
                        <label className="block text-xs md:text-sm font-medium text-slate-500 mb-1">Status</label>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={`inline-block px-2 py-1 text-xs md:text-sm rounded-full ${voucher.is_posted ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                {voucher.is_posted ? 'Posted' : 'Draft'}
                            </span>
                            {!voucher.is_posted && (
                                <button
                                    onClick={handlePost}
                                    disabled={posting}
                                    className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {posting ? '...' : 'Post'}
                                </button>
                            )}
                            {voucher.is_posted && (
                                <button
                                    onClick={handleUnpost}
                                    disabled={posting}
                                    className="px-3 py-1 bg-amber-600 text-white text-xs rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                                >
                                    {posting ? '...' : 'Unpost'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {voucher.narration && (
                    <div className="mt-4">
                        <label className="block text-xs md:text-sm font-medium text-slate-500 mb-1">Narration</label>
                        <p className="text-sm md:text-base text-slate-900">{voucher.narration}</p>
                    </div>
                )}

                <div className="mt-4">
                    <label className="block text-xs md:text-sm font-medium text-slate-500 mb-1">Total Amount</label>
                    <p className="text-xl md:text-2xl font-bold text-slate-900">₹{parseFloat(voucher.total_amount || 0).toFixed(2)}</p>
                </div>
            </div>

            {/* Voucher Entries - Mobile Cards */}
            <div className="block md:hidden bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <h2 className="text-base font-bold text-slate-800">Voucher Entries</h2>
                </div>

                <div className="divide-y divide-slate-100">
                    {voucher.entries && voucher.entries.map((entry, index) => (
                        <div key={index} className="p-4">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <p className="font-semibold text-slate-900">{entry.account_group?.name || 'N/A'}</p>
                                    <p className="text-xs text-slate-500">{entry.account_group?.account_head?.name || '-'}</p>
                                </div>
                            </div>
                            {entry.narration && (
                                <p className="text-xs text-slate-600 mb-2">{entry.narration}</p>
                            )}
                            <div className="flex gap-4">
                                <div>
                                    <span className="text-xs text-slate-500">Debit: </span>
                                    {parseFloat(entry.debit_amount || 0) > 0 ? (
                                        <span className="font-medium text-green-700">₹{parseFloat(entry.debit_amount).toFixed(2)}</span>
                                    ) : (
                                        <span className="text-slate-400">-</span>
                                    )}
                                </div>
                                <div>
                                    <span className="text-xs text-slate-500">Credit: </span>
                                    {parseFloat(entry.credit_amount || 0) > 0 ? (
                                        <span className="font-medium text-red-700">₹{parseFloat(entry.credit_amount).toFixed(2)}</span>
                                    ) : (
                                        <span className="text-slate-400">-</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-between">
                    <span className="font-bold text-slate-800">Total:</span>
                    <div className="flex gap-4">
                        <span className="font-bold text-green-700">
                            ₹{(voucher.entries?.reduce((sum, e) => sum + parseFloat(e.debit_amount || 0), 0) || 0).toFixed(2)}
                        </span>
                        <span className="font-bold text-red-700">
                            ₹{(voucher.entries?.reduce((sum, e) => sum + parseFloat(e.credit_amount || 0), 0) || 0).toFixed(2)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Voucher Entries - Desktop Table */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-800">Voucher Entries</h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-100 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Account Group</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Account Head</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Narration</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">Debit</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">Credit</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {voucher.entries && voucher.entries.map((entry, index) => (
                                <tr key={index} className="hover:bg-slate-50">
                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                        {entry.account_group?.name || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600">
                                        {entry.account_group?.account_head?.name || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600">{entry.narration || '-'}</td>
                                    <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                        {parseFloat(entry.debit_amount || 0) > 0 ? (
                                            <span className="text-green-700">₹{parseFloat(entry.debit_amount).toFixed(2)}</span>
                                        ) : (
                                            <span className="text-slate-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                        {parseFloat(entry.credit_amount || 0) > 0 ? (
                                            <span className="text-red-700">₹{parseFloat(entry.credit_amount).toFixed(2)}</span>
                                        ) : (
                                            <span className="text-slate-400">-</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                            <tr>
                                <td colSpan="3" className="px-6 py-4 font-bold text-slate-800">Total:</td>
                                <td className="px-6 py-4 text-right font-bold text-green-700">
                                    ₹{(voucher.entries?.reduce((sum, e) => sum + parseFloat(e.debit_amount || 0), 0) || 0).toFixed(2)}
                                </td>
                                <td className="px-6 py-4 text-right font-bold text-red-700">
                                    ₹{(voucher.entries?.reduce((sum, e) => sum + parseFloat(e.credit_amount || 0), 0) || 0).toFixed(2)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default VoucherShow;
